import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Button, Chip, Stack, Card, CardContent, Fade } from '@mui/material';
import { IconPhoto, IconMapPin, IconRulerMeasure } from '@tabler/icons';
import { useParams, useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import { encodePathSegments } from '../../../../../utils/media-url';
import { formatImovelPrice } from '../../../../../utils/currency';
import { STATUS_OPTIONS } from './CreateImovel';

const BACKEND_SERVER = process.env.REACT_APP_BACKEND_SERVER || 'http://127.0.0.1:8000/api/';

const STATUS_COLOR = {
    disponivel: 'success',
    pendente: 'warning',
    reservado: 'warning',
    indisponivel: 'default',
    vendido: 'default',
    alugado: 'default',
    encerrado: 'default'
};

const ViewImovel = () => {
    const { id } = useParams();
    const history = useHistory();

    const [imovel, setImovel] = useState(null);
    const [appId, setAppId] = useState(null);
    const [imagemSelecionada, setImagemSelecionada] = useState(0);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadImovel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, id]);

    const loadImovel = async () => {
        const res = await api.get(`v1/apps/${appId}/imoveis/${id}/`);
        setImovel(res.data);
        setImagemSelecionada(0);
    };

    const deleteImovel = async () => {
        await api.delete(`v1/apps/${appId}/imoveis/${id}/`);
        history.push('/dashboard/config/imoveis');
    };

    if (!imovel) {
        return (
            <Box p={4}>
                <Typography>Carregando imóvel...</Typography>
            </Box>
        );
    }

    const customFields = Object.entries(imovel.custom_fields || {});
    const imagens = imovel.imagens || [];
    const getImageUrl = (path) => `${BACKEND_SERVER}v1/apps/${appId}/media/${encodePathSegments(path)}`;

    return (
        <Box sx={{ p: 5, minHeight: '100vh' }}>
            <Fade in timeout={400}>
                <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Typography variant="h3" fontWeight="800">
                            {imovel.titulo}
                        </Typography>
                        <Chip
                            label={STATUS_OPTIONS.find((o) => o.value === imovel.status)?.label || imovel.status}
                            color={STATUS_COLOR[imovel.status] || 'default'}
                            sx={{ fontWeight: 600 }}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center" color="text.secondary" mb={2}>
                        <Typography sx={{ textTransform: 'capitalize' }}>
                            {imovel.tipo} • {imovel.finalidade}
                        </Typography>
                        {(imovel.bairro || imovel.cidade) && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <IconMapPin size={16} />
                                <Typography variant="body2">
                                    {imovel.bairro ? `${imovel.bairro}, ` : ''}
                                    {imovel.cidade}
                                    {imovel.estado ? `/${imovel.estado}` : ''}
                                </Typography>
                            </Stack>
                        )}
                        {(imovel.area_m2 || imovel.area_util_m2) && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <IconRulerMeasure size={16} />
                                <Typography variant="body2">{imovel.area_util_m2 || imovel.area_m2} m²</Typography>
                            </Stack>
                        )}
                    </Stack>

                    <Typography variant="h4" fontWeight="800" color="primary" mb={3}>
                        {formatImovelPrice(imovel.valor, imovel.valor_a_partir_de)}
                    </Typography>

                    {imagens.length > 0 ? (
                        <Card sx={{ borderRadius: 4, mb: 3, overflow: 'hidden' }}>
                            <Box
                                component="img"
                                src={getImageUrl(imagens[imagemSelecionada]?.url)}
                                sx={{ width: '100%', maxHeight: 480, objectFit: 'cover', display: 'block' }}
                            />
                            {imagens.length > 1 && (
                                <Stack direction="row" spacing={1} sx={{ p: 1.5, overflowX: 'auto' }}>
                                    {imagens.map((img, i) => (
                                        <Box
                                            key={img.id}
                                            component="img"
                                            src={getImageUrl(img.url)}
                                            onClick={() => setImagemSelecionada(i)}
                                            sx={{
                                                width: 72,
                                                height: 72,
                                                objectFit: 'cover',
                                                borderRadius: 2,
                                                cursor: 'pointer',
                                                flexShrink: 0,
                                                border: i === imagemSelecionada ? '2px solid' : '2px solid transparent',
                                                borderColor: i === imagemSelecionada ? 'primary.main' : 'transparent'
                                            }}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Card>
                    ) : (
                        <Card sx={{ borderRadius: 4, mb: 3 }}>
                            <Box
                                sx={{
                                    height: 200,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    bgcolor: 'rgba(0,0,0,0.06)',
                                    color: 'text.secondary'
                                }}
                            >
                                <IconPhoto size={32} />
                                <Typography variant="caption">Nenhuma foto cadastrada</Typography>
                            </Box>
                        </Card>
                    )}

                    <Card sx={{ borderRadius: 4, mb: 3 }}>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Endereço
                                    </Typography>
                                    <Typography>{imovel.endereco || '-'}</Typography>
                                </Grid>

                                <Grid item xs={6} sm={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        Bairro
                                    </Typography>
                                    <Typography>{imovel.bairro || '-'}</Typography>
                                </Grid>

                                <Grid item xs={6} sm={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        Cidade/Estado
                                    </Typography>
                                    <Typography>{imovel.cidade ? `${imovel.cidade}${imovel.estado ? '/' + imovel.estado : ''}` : '-'}</Typography>
                                </Grid>

                                <Grid item xs={6} sm={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        Área
                                    </Typography>
                                    <Typography>{imovel.area_m2 ? `${imovel.area_m2} m²` : '-'}</Typography>
                                </Grid>

                                <Grid item xs={6} sm={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        Área útil
                                    </Typography>
                                    <Typography>{imovel.area_util_m2 ? `${imovel.area_util_m2} m²` : '-'}</Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        Descrição
                                    </Typography>
                                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{imovel.descricao || '-'}</Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {customFields.length > 0 && (
                        <Card sx={{ borderRadius: 4, mb: 3 }}>
                            <CardContent>
                                <Typography mb={2}>Campos adicionais</Typography>

                                <Grid container spacing={2}>
                                    {customFields.map(([name, value]) => (
                                        <Grid item xs={6} key={name}>
                                            <Typography variant="body2" color="text.secondary">
                                                {name}
                                            </Typography>
                                            <Typography>{value || '-'}</Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" disabled={!imovel?.id} onClick={() => imovel?.id && history.push(`/dashboard/config/imovel/edit/${imovel.id}`)}>
                            Editar
                        </Button>

                        <Button variant="outlined" onClick={() => history.push('/dashboard/config/imoveis')}>
                            Voltar
                        </Button>

                        <Button variant="outlined" color="error" onClick={deleteImovel}>
                            Deletar
                        </Button>
                    </Stack>
                </Box>
            </Fade>
        </Box>
    );
};

export default ViewImovel;
